import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import { defineMessages } from "react-intl"
import { BulkActionDefinition, BulkActionType, ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import GlobalHelper from "../../../../common/GlobalHelper"
import { Permissions } from "../../../../role/Guard"
import { BULK_OPERATION_MESSAGES, BULK_OPERATION_TYPE, isContactBulkExportSupported } from "../../../../utils/automation/bulk"
import { PREFERENCE_TYPE } from "../../constants"
    
export enum ContactsColumnIds {
    NAME = "name",
    ORGANIZATION_NAME = "organizationName",
    CUSTOMER_TYPE = "customerTypeName",
    CUSTOMER_TAGS = "tagIds",
    CITY = "city",
    PHONE_NUMBER = "phoneNumber",
    CREATED_AT_DATE = "createdAt",
    UPDATED_AT_DATE = "updatedAt",
    FIRST_NAME = "firstName",
    LAST_NAME = "lastName",
    TYPE = "contactType",
    TAX_NUMBER = "taxNumber",
    EMAIL = "email",
    DEFAULT_TAX = "taxId",
    RECURRING_STATEMENT_ENABLED = "recurringStatementEnabled",
    RECURRING_STATEMENT_NEXT_CREATION_DATE = "recurringStatementNextCreationDate",
    RECURRING_STATEMENT_SEND_EMAIL_ENABLED = "recurringStatementSendEmail",
    RECURRING_STATEMENT_AUTO_CAPTURE_PAYMENT = "recurringStatementCapturePayment",
    RECURRING_STATEMENT_WITH_ATTACHED_PDF = "recurringStatementAttachPdf",
    ARCHIVED = "isArchived",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: ContactsColumnIds.NAME,
        sortId: "nameKeyword",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/contacts/{id}/details"
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.ORGANIZATION_NAME,
        sortId: "organizationNameKeyword",
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/contacts/{organizationId}/details"
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.CUSTOMER_TYPE,
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.CUSTOMER_TAGS,
        dataType: DataType.ARRAY,
        dataAttributes: {
            optionsKey: "labelOptions",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.CITY,
        accessorFn: row => {
            const address = {
                city: row.city,
                stateCode: row.stateCode,
                state: row.state,
                country: row.country,
                countryCode: row.countryCode,
            }
            return GlobalHelper.formatCityFromAddress(address, AppContext.getLocale().getLanguageWithoutRegionCode())
        },
        sortId: "cityKeyword",
        dataType: DataType.STRING,
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.PHONE_NUMBER,
        accessorFn: row => {
            if (row.phoneNumber?.length) {
                return row.phoneNumber[0]
            }
            return ""
        },
        sortId: "phoneNumberKeyword",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ContactsColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: ContactsColumnIds.FIRST_NAME,
        sortId: "firstNameKeyword",
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.LAST_NAME,
        sortId: "lastNameKeyword",
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.TAX_NUMBER,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.EMAIL,
        sortId: "emailKeyword",
        dataType: DataType.ARRAY,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.DEFAULT_TAX,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.RECURRING_STATEMENT_ENABLED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.RECURRING_STATEMENT_NEXT_CREATION_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.RECURRING_STATEMENT_SEND_EMAIL_ENABLED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.RECURRING_STATEMENT_AUTO_CAPTURE_PAYMENT,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.RECURRING_STATEMENT_WITH_ATTACHED_PDF,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: ContactsColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
]

const headerMessages = defineMessages({
    [ContactsColumnIds.NAME]: {
        id: "generic-listing.contacts.table-header.name",
        defaultMessage: "Name",
    },
    [ContactsColumnIds.ORGANIZATION_NAME]: {
        id: "generic-listing.contacts.table-header.organization-name",
        defaultMessage: "Company name",
    },
    [ContactsColumnIds.CUSTOMER_TYPE]: {
        id: "generic-listing.contacts.table-header.customer-type",
        defaultMessage: "Customer type",
    },
    [ContactsColumnIds.CUSTOMER_TAGS]: {
        id: "generic-listing.contacts.table-header.customer-tags",
        defaultMessage: "Customer tags",
    },
    [ContactsColumnIds.CITY]: {
        id: "generic-listing.contacts.table-header.city",
        defaultMessage: "City",
    },
    [ContactsColumnIds.PHONE_NUMBER]: {
        id: "generic-listing.contacts.table-header.phone-number",
        defaultMessage: "Phone number",
    },
    [ContactsColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.contacts.table-header.created-at-date",
        defaultMessage: "Created at",
    },
    [ContactsColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.contacts.table-header.update-at-date",
        defaultMessage: "Updated at",
    },
    [ContactsColumnIds.FIRST_NAME]: {
        id: "generic-listing.contacts.table-header.first-name",
        defaultMessage: "First Name",
    },
    [ContactsColumnIds.LAST_NAME]: {
        id: "generic-listing.contacts.table-header.last-name",
        defaultMessage: "Last Name",
    },
    [ContactsColumnIds.TAX_NUMBER]: {
        id: "generic-listing.contacts.table-header.tax-number",
        defaultMessage: "Tax Number",
    },
    [ContactsColumnIds.EMAIL]: {
        id: "generic-listing.contacts.table-header.email",
        defaultMessage: "Email",
    },
    [ContactsColumnIds.DEFAULT_TAX]: {
        id: "generic-listing.contacts.table-header.default-tax",
        defaultMessage: "Default Tax",
    },
    [ContactsColumnIds.RECURRING_STATEMENT_ENABLED]: {
        id: "generic-listing.contacts.table-header.recurring-statement-enabled",
        defaultMessage: "Recurring Statement Enabled",
    },
    [ContactsColumnIds.RECURRING_STATEMENT_NEXT_CREATION_DATE]: {
        id: "generic-listing.contacts.table-header.recurring-statement-next-creation-date",
        defaultMessage: "Recurring Statement Next Creation Date",
    },
    [ContactsColumnIds.RECURRING_STATEMENT_SEND_EMAIL_ENABLED]: {
        id: "generic-listing.contacts.table-header.recurring-statement-send-email-enabled",
        defaultMessage: "Recurring Statement Send Email Enabled",
    },
    [ContactsColumnIds.RECURRING_STATEMENT_AUTO_CAPTURE_PAYMENT]: {
        id: "generic-listing.contacts.table-header.recurring-statement-auto-capture-payment",
        defaultMessage: "Recurring Statement Auto Capture Payment",
    },
    [ContactsColumnIds.RECURRING_STATEMENT_WITH_ATTACHED_PDF]: {
        id: "generic-listing.contacts.table-header.recurring-statement-with-attached-pdf",
        defaultMessage: "Recurring Statement With Attached PDF",
    },
    [ContactsColumnIds.ARCHIVED]: {
        id: "generic-listing.contacts.table-header.archived",
        defaultMessage: "Archived",
    },
})

const bulkActionDefinitions: BulkActionDefinition[] = [
    {
        id: BULK_OPERATION_TYPE.CREATE_ACCOUNT_STATEMENT,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.CREATE_ACCOUNT_STATEMENT],
        icon: "file-invoice",
    },
    {
        id: BULK_OPERATION_TYPE.CONTACT_UPDATE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.CONTACT_UPDATE],
        icon: "edit",
    },
    {
        id: BULK_OPERATION_TYPE.EXPORT,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.EXPORT],
        icon: "file-export",
        permission: isContactBulkExportSupported(),
    },
    {
        id: BULK_OPERATION_TYPE.DELETE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.DELETE],
        icon: "trash",
        type: BulkActionType.DANGER,
        guard: Permissions.DeleteContact
    },
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.CONTACTS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_CONTACTS,
    searchPlaceholder: {
        id: "generic-listing.contacts.search-placeholder",
        defaultMessage: "Search a contact...",
    },
    displayString: {
        id: "generic-listing.contacts.display-string",
        defaultMessage: "Contacts",
    },
    searchObjectType: ObjectType.CONTACTS,
    bulkActionDefinitions,
    additionalFilters: {
        contactType: ["persons"]
    },
    exportModalComponent: require("../../../../common/listing/generic-listing/export-modals/GenericExportModal"),
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/ContactCreationButton"),
}

export default configuration


// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/contacts.ts